import { isNil } from 'lodash-es';
import {
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
  useId,
  type ReactElement,
  Children,
  isValidElement,
} from 'react';

import {
  type DataTestId,
  type StylingProps,
  type DOMProps,
  type MaskingProps,
  type AriaLabelingProps,
  type AriaDisabledProps,
} from '@dynatrace/strato-components/core';
import {
  Flex,
  _useContainerColoring as useContainerColoring,
} from '@dynatrace/strato-components/layouts';

import { type HealthIndicatorLabelProps, Label } from './Label.js';
import { StatusVisual } from './StatusVisual.js';
import * as styles from './styles.sty.js';
import { type HealthIndicatorVisualProps, Visual } from './Visual.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { isSlot } from '../../core/utils/is-slot.js';

// #region Typings
/**
 * Accepted properties for HealthIndicator
 * @public
 */
export interface HealthIndicatorProps
  extends DOMProps,
    StylingProps,
    DataTestId,
    PropsWithChildren,
    MaskingProps,
    AriaLabelingProps,
    AriaDisabledProps {
  /**
   * The status of the component. This should be chosen based on the context
   * the indicator is used in.
   * @defaultValue 'neutral'
   */
  status?: 'ideal' | 'good' | 'neutral' | 'warning' | 'critical';
  /**
   * Configures the visual representation of the component, which is a shape by
   * default.
   * @defaultValue 'shape'
   */
  visual?: 'shape' | 'icon';
}

/** Defines slots into which children of the HealthIndicator should be rendered. */
type HealthIndicatorSlots = {
  visual: ReactElement<HealthIndicatorVisualProps> | null;
  label: ReactElement<HealthIndicatorLabelProps> | null;
};
// #endregion

/**
 * Iterates over child nodes to extract specific component slots.
 */
const getHealthIndicatorSlots = (children: ReactNode): HealthIndicatorSlots => {
  const slots: HealthIndicatorSlots = {
    visual: null,
    label: null,
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Visual)) {
      slots.visual = child;
    } else if (isSlot(child, Label)) {
      slots.label = child;
    } else if (isValidElement(child) && isDev()) {
      // eslint-disable-next-line no-console
      console.warn(
        "Only 'Visual' and 'Label' are valid children of the HealthIndicator.",
      );
    }
  });

  return slots;
};

const HealthIndicatorRoot = forwardRef<HTMLDivElement, HealthIndicatorProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      id,
      status = 'neutral',
      visual = 'shape',
      'aria-labelledby': ariaLabelledBy,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'data-testid': dataTestId,
      ...remainingProps
    }: HealthIndicatorProps = props;

    const containerColoring = useContainerColoring();
    const onAccent = containerColoring.variant === 'accent';

    const healthIndicatorProps = {
      id,
      ref: forwardedRef,
      style: {
        minWidth: 0,
        ...consumerStyle,
      },
      className: consumerClassName,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-testid': dataTestId,
      ...remainingProps,
    };

    const labelId = useId();
    const slots = getHealthIndicatorSlots(children);

    const visualContainerHeight = visual === 'icon' ? 10 : 6;

    return (
      <Flex
        alignItems="flex-start"
        gap={visual === 'icon' ? 8 : 4}
        {...healthIndicatorProps}
      >
        <div
          className={styles.visual({ status, onAccent, visualType: visual })}
          aria-labelledby={
            ariaLabelledBy ?? (slots.label && !ariaLabel ? labelId : undefined)
          }
          aria-label={ariaLabel}
        >
          {!isNil(slots.visual) ? (
            slots.visual
          ) : (
            <StatusVisual status={status} visual={visual} />
          )}
        </div>
        {!isNil(slots.label) && (
          <div
            id={labelId}
            style={{
              // Calculate padding-top: (container height for icon (20px) or shape (12px) - 1 line height) / 2
              paddingTop: `calc(-0.5lh + ${visualContainerHeight}px)`,
              minWidth: '0',
            }}
          >
            {slots.label}
          </div>
        )}
      </Flex>
    );
  },
);

(
  HealthIndicatorRoot as typeof HealthIndicatorRoot & { displayName: string }
).displayName = 'HealthIndicator';

/**
 * The HealthIndicator helps consumers to visualize the status of a resource.
 * @public
 */
export const HealthIndicator = Object.assign(HealthIndicatorRoot, {
  Visual,
  Label,
});
